import {ModalState} from "../../../types/modals";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {deleteResult, updateResults} from "../adminList";
import {toastSuccess} from "../admin";


export type TeamModalState = ModalState & {
    team_id: string | null
    name: string
    domains: string[]
    url: string
    users: number
    delete_message: string
}

const initialState: TeamModalState = {
    loading: true,
    isOpen: false,
    error: null,
    team_id: null,
    name: '',
    domains: [],
    url: '',
    users: 0,
    delete_message: '',
}

export type NewTeamPayload = {
    name: string
    domains: string[]
}

export type EditTeamPayload = NewTeamPayload & {
    team_id: string
}


export const getTeam = createAsyncThunk(
    'teamModal/getTeam',
    async (team_id: string, thunkAPI) => {
        try {
            return await api.get(`/API/admin/teams/${team_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const deleteTeam = createAsyncThunk(
    'teamModal/deleteTeam',
    async (team_id: string, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.delete(`/API/admin/teams/${team_id}/`);
            if(response.key){
                dispatch(deleteResult({...response}));
            }
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveNewTeam = createAsyncThunk(
    'teamModal/saveNewTeam',
    async (payload: NewTeamPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.post(`/API/admin/teams/`, payload);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveTeam = createAsyncThunk(
    'teamModal/saveTeam',
    async (payload: EditTeamPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.patch(`/API/admin/teams/${payload.team_id}/`, payload);
            dispatch(updateResults({ result: response }));
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type ModalPayload = {
    team_id: string | null
}

const slice = createSlice({
    name: 'teamModal',
    initialState,
    reducers: {
        closeModal: () => {
            return initialState;
        },
        openModal: (state, action: PayloadAction<ModalPayload>) => {
            if (action.payload.team_id) {
                state.team_id = action.payload.team_id;
            } else{
                state.loading = false
            }
            state.isOpen = true;
        },
        changeModal: (state, action: PayloadAction<Partial<TeamModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(deleteTeam.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteTeam.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteTeam.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getTeam.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTeam.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(getTeam.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewTeam.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewTeam.fulfilled, () => {
                return initialState;
            })
            .addCase(saveNewTeam.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveTeam.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveTeam.fulfilled, () => {
                return initialState;
            })
            .addCase(saveTeam.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });
    }
});

export const {
    closeModal,
    openModal,
    changeModal
} = slice.actions;

export default slice.reducer;